import * as XLSX from 'xlsx';
import { OptimizationResults, OptimizationResultsWithStats } from '../types';
import { HistoryEntry } from './historyStorage';

/**
 * Generates analysis report text from optimization results
 */
export function generateAnalysisText(results: OptimizationResults): string {
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;

  const lines: string[] = [];
  lines.push('='.repeat(80));
  lines.push('ANALYSIS RESULTS');
  lines.push('='.repeat(80));
  lines.push(`Configuration: Tasks=${results.configuration.taskCount}, Properties=${results.configuration.propertyCount}, Population=${results.configuration.initialPopulation}`);
  lines.push('');

  // Fitness comparison
  lines.push('1. FITNESS COMPARISON (Higher is Better)');
  lines.push('-'.repeat(80));
  const fitnessData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    fitness: results[key].fitness,
  })).sort((a, b) => b.fitness - a.fitness);
  lines.push(`Winner: ${fitnessData[0].name} with Fitness = ${fitnessData[0].fitness.toFixed(6)}`);
  lines.push('');
  lines.push('Ranking:');
  fitnessData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.fitness.toFixed(6)}`);
  });
  lines.push('');

  // Energy comparison
  lines.push('2. ENERGY CONSUMPTION COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const energyData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    energy: results[key].energy,
  })).sort((a, b) => a.energy - b.energy);
  lines.push(`Winner: ${energyData[0].name} with Energy = ${energyData[0].energy.toFixed(6)} Joules`);
  lines.push('');
  lines.push('Ranking:');
  energyData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.energy.toFixed(6)} Joules`);
  });
  lines.push('');

  // Response Time comparison
  lines.push('3. RESPONSE TIME COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const responseTimeData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    responseTime: results[key].responseTime,
  })).sort((a, b) => a.responseTime - b.responseTime);
  lines.push(`Winner: ${responseTimeData[0].name} with Response Time = ${responseTimeData[0].responseTime.toFixed(2)} ms`);
  lines.push('');
  lines.push('Ranking:');
  responseTimeData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.responseTime.toFixed(2)} ms`);
  });
  lines.push('');

  // Execution Cost comparison
  lines.push('4. EXECUTION COST COMPARISON (Lower is Better)');
  lines.push('-'.repeat(80));
  const costData = algorithmKeys.map(key => ({
    name: algorithms[algorithmKeys.indexOf(key)],
    cost: results[key].cost,
  })).sort((a, b) => a.cost - b.cost);
  lines.push(`Winner: ${costData[0].name} with Cost = ${costData[0].cost.toFixed(6)}`);
  lines.push('');
  lines.push('Ranking:');
  costData.forEach((alg, idx) => {
    lines.push(`  ${idx + 1}. ${alg.name.padEnd(10)} : ${alg.cost.toFixed(6)}`);
  });
  lines.push('');

  return lines.join('\n');
}

/**
 * Exports optimization results to Excel file
 */
export function exportToExcel(results: OptimizationResults, filename: string = 'optimization_results.xlsx'): void {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Fitness Results
  const fitnessData = [
    ['Algorithm', 'Fitness', 'Energy (Joules)', 'Cost', 'Response Time (ms)'],
    ['GA', results.ga.fitness, results.ga.energy, results.ga.cost, results.ga.responseTime],
    ['Cultural', results.culture.fitness, results.culture.energy, results.culture.cost, results.culture.responseTime],
    ['SFLA', results.frog.fitness, results.frog.energy, results.frog.cost, results.frog.responseTime],
    ['SFLAGA', results.sflaga.fitness, results.sflaga.energy, results.sflaga.cost, results.sflaga.responseTime],
    ['PSO', results.pso.fitness, results.pso.energy, results.pso.cost, results.pso.responseTime],
    ['GAPSO', results.gapso.fitness, results.gapso.energy, results.gapso.cost, results.gapso.responseTime],
  ];
  const fitnessSheet = XLSX.utils.aoa_to_sheet(fitnessData);
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Results');

  // Sheet 2: Solutions
  const solutionsData: (string | number)[][] = [
    ['Algorithm', ...Array.from({ length: results.ga.solution.length }, (_, i) => `Task ${i + 1}`)],
  ];
  
  const algorithms = [
    { name: 'GA', solution: results.ga.solution },
    { name: 'Cultural', solution: results.culture.solution },
    { name: 'SFLA', solution: results.frog.solution },
    { name: 'SFLAGA', solution: results.sflaga.solution },
    { name: 'PSO', solution: results.pso.solution },
    { name: 'GAPSO', solution: results.gapso.solution },
  ];

  algorithms.forEach(({ name, solution }) => {
    solutionsData.push([name, ...solution]);
  });

  const solutionsSheet = XLSX.utils.aoa_to_sheet(solutionsData);
  XLSX.utils.book_append_sheet(workbook, solutionsSheet, 'Solutions');

  // Sheet 3: Configuration
  const configData = [
    ['Parameter', 'Value'],
    ['Task Count', results.configuration.taskCount],
    ['Property Count', results.configuration.propertyCount],
    ['Initial Population', results.configuration.initialPopulation],
    ['Crossover Rate', results.configuration.crossover],
    ['Mutation Rate', results.configuration.mutation],
  ];
  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration');

  // Write file
  XLSX.writeFile(workbook, filename);
}

/**
 * Exports analysis report to text file
 */
export function exportAnalysisReport(analysisText: string, filename: string = 'analysis_report.txt'): void {
  const blob = new Blob([analysisText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exports statistics results to Excel file
 */
export function exportStatisticsToExcel(results: OptimizationResultsWithStats, filename: string = 'statistics_results.xlsx'): void {
  if (!results.statistics) {
    throw new Error('No statistics available to export');
  }

  const workbook = XLSX.utils.book_new();
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;

  // Helper function to create statistics sheet for a metric
  const createStatisticsSheet = (metricName: string, metricKey: 'fitness' | 'energy' | 'cost' | 'responseTime' | 'executionTime') => {
    const header: (string | number)[] = ['Algorithm', 'Mean', 'Std Dev', 'Min', 'Max', 'Median'];
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const stats = results.statistics![key][metricKey];
      data.push([
        algorithms[idx],
        stats.mean,
        stats.stdDev,
        stats.min,
        stats.max,
        stats.median,
      ]);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      { wch: 15 }, // Mean
      { wch: 15 }, // Std Dev
      { wch: 15 }, // Min
      { wch: 15 }, // Max
      { wch: 15 }, // Median
    ];

    return sheet;
  };

  // Fitness Statistics Sheet
  const fitnessSheet = createStatisticsSheet('Fitness', 'fitness');
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Statistics');

  // Energy Statistics Sheet
  const energySheet = createStatisticsSheet('Energy', 'energy');
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy Statistics');

  // Cost Statistics Sheet
  const costSheet = createStatisticsSheet('Cost', 'cost');
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Statistics');

  // Response Time Statistics Sheet
  const responseTimeSheet = createStatisticsSheet('Response Time', 'responseTime');
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time Statistics');

  // Execution Time Statistics Sheet
  const executionTimeSheet = createStatisticsSheet('Execution Time', 'executionTime');
  XLSX.utils.book_append_sheet(workbook, executionTimeSheet, 'Execution Time Statistics');

  // Summary Sheet
  const summaryData = [
    ['Summary Information'],
    ['Number of Runs', results.runNumber || 1],
    ['Timestamp', results.timestamp ? new Date(results.timestamp).toLocaleString() : 'N/A'],
    ['Task Count', results.configuration.taskCount],
    ['Property Count', results.configuration.propertyCount],
    ['Initial Population', results.configuration.initialPopulation],
    ['Crossover Rate', results.configuration.crossover],
    ['Mutation Rate', results.configuration.mutation],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Write file
  XLSX.writeFile(workbook, filename);
}

/**
 * Exports comparison results to Excel file
 */
export function exportComparisonToExcel(entries: HistoryEntry[], filename: string = 'comparison_results.xlsx'): void {
  if (entries.length === 0) {
    throw new Error('No entries to export');
  }

  const workbook = XLSX.utils.book_new();
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;

  // Helper function to create comparison sheet for a metric
  const createComparisonSheet = (
    metricName: string,
    getValue: (result: OptimizationResults, key: typeof algorithmKeys[number]) => number,
    unit: string = ''
  ) => {
    const header: (string | number)[] = ['Algorithm'];
    entries.forEach((entry, idx) => {
      header.push(`Run ${idx + 1} (${new Date(entry.timestamp).toLocaleDateString()})`);
    });
    const data: (string | number)[][] = [header];

    algorithmKeys.forEach((key, idx) => {
      const row: (string | number)[] = [algorithms[idx]];
      entries.forEach((entry) => {
        row.push(getValue(entry.results, key));
      });
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Algorithm
      ...entries.map(() => ({ wch: 25 })), // Each run column
    ];

    return sheet;
  };

  // Fitness Comparison Sheet
  const fitnessSheet = createComparisonSheet(
    'Fitness',
    (result, key) => result[key].fitness
  );
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness Comparison');

  // Energy Comparison Sheet
  const energySheet = createComparisonSheet(
    'Energy',
    (result, key) => result[key].energy,
    'J'
  );
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy Comparison');

  // Cost Comparison Sheet
  const costSheet = createComparisonSheet(
    'Cost',
    (result, key) => result[key].cost
  );
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Comparison');

  // Response Time Comparison Sheet
  const responseTimeSheet = createComparisonSheet(
    'Response Time',
    (result, key) => result[key].responseTime,
    'ms'
  );
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time Comparison');

  // Configuration Comparison Sheet
  const configHeader: (string | number)[] = ['Parameter'];
  entries.forEach((entry, idx) => {
    configHeader.push(`Run ${idx + 1} (${new Date(entry.timestamp).toLocaleDateString()})`);
  });
  const configData: (string | number)[][] = [
    configHeader,
    ['Task Count', ...entries.map(e => e.results.configuration.taskCount)],
    ['Property Count', ...entries.map(e => e.results.configuration.propertyCount)],
    ['Initial Population', ...entries.map(e => e.results.configuration.initialPopulation)],
    ['Crossover Rate', ...entries.map(e => e.results.configuration.crossover)],
    ['Mutation Rate', ...entries.map(e => e.results.configuration.mutation)],
    ['Number of Runs', ...entries.map(e => {
      const results = e.results as OptimizationResultsWithStats;
      return results.runNumber || 1;
    })],
  ];
  const configSheet = XLSX.utils.aoa_to_sheet(configData);
  XLSX.utils.book_append_sheet(workbook, configSheet, 'Configuration Comparison');

  // Write file
  XLSX.writeFile(workbook, filename);
}

export interface CompleteRunResult {
  taskCount: number;
  results: OptimizationResults;
  timestamp: string;
}

/**
 * Exports complete run results to Excel file
 */
export function exportCompleteRunToExcel(
  completeRunResults: CompleteRunResult[],
  filename: string = 'complete_run_results.xlsx'
): void {
  if (completeRunResults.length === 0) {
    throw new Error('No results to export');
  }

  const workbook = XLSX.utils.book_new();
  const algorithms = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;

  // Helper function to create a metric sheet across all task counts
  const createMetricSheet = (
    metricName: string,
    getValue: (result: OptimizationResults, key: typeof algorithmKeys[number]) => number,
    unit: string = ''
  ) => {
    const header: (string | number)[] = ['Task Count', ...algorithms];
    const data: (string | number)[][] = [header];

    completeRunResults.forEach((entry) => {
      const row: (string | number)[] = [entry.taskCount];
      algorithmKeys.forEach((key) => {
        row.push(getValue(entry.results, key));
      });
      data.push(row);
    });

    const sheet = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    sheet['!cols'] = [
      { wch: 12 }, // Task Count
      ...algorithms.map(() => ({ wch: 15 })), // Algorithm columns
    ];

    return sheet;
  };

  // Fitness Sheet
  const fitnessSheet = createMetricSheet('Fitness', (result, key) => result[key].fitness);
  XLSX.utils.book_append_sheet(workbook, fitnessSheet, 'Fitness');

  // Energy Sheet
  const energySheet = createMetricSheet('Energy', (result, key) => result[key].energy, 'J');
  XLSX.utils.book_append_sheet(workbook, energySheet, 'Energy');

  // Cost Sheet
  const costSheet = createMetricSheet('Cost', (result, key) => result[key].cost);
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost');

  // Response Time Sheet
  const responseTimeSheet = createMetricSheet(
    'Response Time',
    (result, key) => result[key].responseTime,
    'ms'
  );
  XLSX.utils.book_append_sheet(workbook, responseTimeSheet, 'Response Time');

  // Execution Time Sheet
  const executionTimeSheet = createMetricSheet(
    'Execution Time',
    (result, key) => result[key].executionTime || 0,
    'ms'
  );
  XLSX.utils.book_append_sheet(workbook, executionTimeSheet, 'Execution Time');

  // Summary Sheet
  const summaryData = [
    ['Complete Run Summary'],
    ['Total Runs', completeRunResults.length],
    ['Start Date', new Date(completeRunResults[0].timestamp).toLocaleString()],
    ['End Date', new Date(completeRunResults[completeRunResults.length - 1].timestamp).toLocaleString()],
    [''],
    ['Task Count Range'],
    ['Min', Math.min(...completeRunResults.map(r => r.taskCount))],
    ['Max', Math.max(...completeRunResults.map(r => r.taskCount))],
    [''],
    ['Task Counts'],
    ...completeRunResults.map(r => [r.taskCount]),
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // Write file
  XLSX.writeFile(workbook, filename);
}
